import { Col, Form, Row, Spin, message } from "antd";
import dayjs from "dayjs";
import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import styled from "styled-components";
import { CardContainer } from "../../../components/Card/CardContainer";
import PageTitleNested from "../../../components/PageTitle/PageTitleNested";
import Text from "../../../components/Text/Text";
import { roleDatasource } from "../../../datasource/RoleDatasource";
import color from "../../../resource/color";
import { profileAtom } from "../../../store/ProfileAtom";
import { defaultPropsForm } from "../../../utility/DefaultProps";
import RolesList from "../../../components/RolesList/RolesList";
import { AdditionalRole, additionalRoleLabel } from "../../../definitions/role";
import RoleSaleApp from "../../../components/RolesList/RoleSaleApp";
import Button from "../../../components/Button/Button";
import { numberFormatter } from "../../../utility/Formatter";
import { EyeOutlined, ReloadOutlined } from "@ant-design/icons";
import ModalUserList from "../../../components/Modal/ModalUserList";

const CardRole = styled.div`
  border: 1px solid ${color.background1};
  border-radius: 8px;
  margin-bottom: 24px;
`;

function DetailRole() {
  const { roleId } = useParams();
  const profile = useRecoilValue(profileAtom);
  const [form] = Form.useForm();

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const [activeKey, setActiveKey] = useState<string>();
  const [menuBo, setMenuBO] = useState<any>();
  const [menuSaleApp, setMenuSaleApp] = useState<any>();
  const [checkedValues, setCheckedValues] = useState({
    "Website Back Office": true,
    "Sale Application": false,
  });
  const [openUserList, setOpenUserList] = useState(false);

  const fetchData = useCallback(async () => {
    if (!roleId || !profile?.company) return;
    try {
      setLoading(true);
      const res = await roleDatasource.getRoleById(roleId, profile.company);
      setData(res);
    } catch (error) {
      console.error(error);
      message.error("เกิดข้อผิดพลาดในการโหลดข้อมูล");
    } finally {
      setLoading(false);
    }
  }, [roleId, profile?.company]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!data) return;
    form.resetFields();
    const { menus, menusSaleApp } = data;
    const parsedMenus = Array.isArray(menus) ? menus : JSON.parse(menus || "[]");
    const parsedMenusSaleApp = Array.isArray(menusSaleApp)
      ? menusSaleApp
      : JSON.parse(menusSaleApp || "[]");

    setMenuBO(parsedMenus);
    setMenuSaleApp(parsedMenusSaleApp);
    setActiveKey(data.isBo ? "Website Back Office" : "Sale Application");
    setCheckedValues({
      "Website Back Office": data.isBo,
      "Sale Application": data.isSaleApp,
    });

    const findPermission = (key: string) =>
      parsedMenus.find((item) => item.menuName === key)?.permission;
    form.setFieldsValue({
      ...data,
      freebieList: findPermission("freebieList"),
      order: findPermission("order"),
      manageStore: findPermission("manageStore"),
      manageUser: findPermission("manageUser"),
      priceSpecialExclusive: findPermission("priceSpecialExclusive"),
      productList: findPermission("productList"),
      specialRequest: findPermission("specialRequest"),
      promotionSetting: findPermission("promotionSetting"),
      news: findPermission("news"),
      discountCo: findPermission("discountCo"),
      saleManagement: findPermission("saleManagement"),
      roleManagement: findPermission("roleManagement"),
      generalSettings: findPermission("generalSettings"),
      report: findPermission("report"),
      receiveGoods: findPermission("receiveGoods"),
      productQuota: findPermission("productQuota"),
      crm: findPermission("crm"),
    });
  }, [data, form]);

  if (loading && !data) {
    return (
      <CardContainer style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Spin size='large' />
      </CardContainer>
    );
  }

  if (!data) return null;

  return (
    <CardContainer>
      <PageTitleNested title='รายละเอียดบทบาท' cutParams />

      <div style={{ marginTop: 16 }}>
        <Row gutter={16} style={{ marginBottom: 8 }}>
          <Col span={3}>
            <Text fontFamily='Sarabun' color='Text1' fontWeight={700}>
              ชื่อบทบาท
            </Text>
          </Col>
          <Col span={21}>
            <Text fontFamily='Sarabun'>{data.rolename}</Text>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: 8 }}>
          <Col span={3}>
            <Text fontFamily='Sarabun' color='Text1' fontWeight={700}>
              คำอธิบายบทบาท
            </Text>
          </Col>
          <Col span={21}>
            <Text fontFamily='Sarabun'>{data.roledescription}</Text>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: 8 }}>
          <Col span={3}>
            <Text fontFamily='Sarabun' color='Text1' fontWeight={700}>
              อัปเดตล่าสุด
            </Text>
          </Col>
          <Col span={21}>
            <Text fontFamily='Sarabun'>
              {`${dayjs(data.updateDate).format("DD/MM/BBBB")} ${data.updateBy}`}
            </Text>
          </Col>
        </Row>
      </div>

      <div
        style={{
          backgroundColor: color.background1,
          width: "max-content",
          padding: "24px",
          borderRadius: "8px",
          margin: "20px 0",
        }}
      >
        <Row gutter={[16, 16]} align='middle'>
          <Col>
            <Text fontFamily='Sarabun'>
              จำนวนสมาชิก : {numberFormatter(data.countmember || 0, 0)} คน
            </Text>
          </Col>
          <Col>
            <Button
              title='ดูรายชื่อสมาชิก'
              icon={<EyeOutlined />}
              onClick={() => setOpenUserList(true)}
            />
          </Col>
        </Row>
      </div>

      <Form form={form} {...defaultPropsForm}>
        <Row style={{ padding: "16px 0" }}>
          <Text fontWeight={700}>สิทธิการใช้งานแพลตฟอร์มต่างๆ</Text>
        </Row>

        <div style={{ display: "flex", margin: "10px 0 20px 0", gap: 10 }}>
          {Object.entries(checkedValues).map(([key, value], index) =>
            value ? (
              <Button
                key={key}
                title={key.replace(/([a-z])([A-Z])/g, "$1 $2")}
                onClick={() => setActiveKey(key)}
                typeButton={
                  activeKey
                    ? activeKey === key
                      ? "navy-blue"
                      : "primary-light"
                    : index === 0
                      ? "navy-blue"
                      : "primary-light"
                }
                style={{ width: "max-content" }}
              />
            ) : null,
          )}
        </div>

        <CardRole>
          {activeKey === "Website Back Office" ? (
            <RolesList callBack={setMenuBO} initialMenu={menuBo} disabled />
          ) : (
            <RoleSaleApp callBack={setMenuSaleApp} initialMenu={menuSaleApp} disabled />
          )}
        </CardRole>
      </Form>

      <ModalUserList
        visible={openUserList}
        onCancel={() => setOpenUserList(false)}
        roleName={data.rolename}
      />
    </CardContainer>
  );
}

export default DetailRole;
